import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { OverlayTrigger, Tooltip, Modal, Button } from 'react-bootstrap';
import TagManager from 'react-gtm-module';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { SessionState, setUser } from 'features/session/sessionSlice';
import { navigateTop, userAnswers, generateWordDocument, generatePdf } from 'common/utils';
import { ReactComponent as StepsRobot } from 'assets/session/dashboard/steps_robot.svg';
import SelfIntroduction from './SelfIntroduction';
import {
  useGetPersonalStatementQuestionsQuery,
  useGetSchoolsPersonalStatementQuery,
  useLazyGetSchoolProgramsPersonalStatementQuery,
  usePatchPersonalStatementMutation,
  useGeneratePersonalStatementMutation,
  useGetPersonalStatementQuery,
  useUpdateUserFromPersonalStatementMutation,
  useLazyGetPostulationsListQuery,
  useAddPostulationFilesMutation,
  useSendPersonalStatementMailFileMutation,
} from 'services/exploreApi';
import Loader from 'common/components/Loader/Loader';

import styles from './PersonalStatementBuilder.module.scss';

import Text from 'common/components/Text/Text';
import { CommonHash, PersonalStatementQuestion, Postulation } from 'app/types';
import { UniversityProgramStep } from './components/UniversityProgramStep';
import { MotivationsStep } from './components/MotivationsStep';
import PersonalExperience from './PersonalExperience';
import { FutureSkillsStep } from './components/FutureSkillsStep';

import OptionalQuestion from './OptionalQuestion';
import { ReactComponent as RobotShadowIcon } from 'assets/profile/shadow_robot.svg';
import PersonalStatementLoader from 'common/components/PersonalStatementLoader/PersonalStatementLoader';

interface TabProps {
  text: string;
  stepIsActive: boolean;
  completed: boolean;
  onClick: React.MouseEventHandler;
}

interface ChecklistItemProps {
  text: string;
}
interface PostulationItemProps {
  postulation: Postulation;
  index: number;
  responseGpt: string;
  psName: string;
  sendGTMEvent: (eventName: string) => void;
  setShowApplicationModal: React.Dispatch<React.SetStateAction<boolean>>;
}

interface LabelInputProps {
  keyNumber: string;
  setInputFunction: (
    key: string,
    value: any,
    setStateFunction: (value: React.SetStateAction<CommonHash>) => void
  ) => void;
  setObjectFunction: React.Dispatch<React.SetStateAction<CommonHash>>;
  inputValue: string;
  placeholderText: string;
  canBeDeleted: boolean;
  deleteBulletInputOption: (
    key: string,
    setStateFunction: (value: React.SetStateAction<CommonHash>) => void,
    stateToChange: CommonHash,
    dependencyTags?: string[],
    setAvailableTags?: (value: React.SetStateAction<string[]>) => void
  ) => void;
  stateToChange: CommonHash;
  dependencyTags?: string[];
  setAvailableTags?: React.Dispatch<React.SetStateAction<string[]>>;
}

const ChecklistItemComponent = ({ text }: ChecklistItemProps): React.ReactElement => {
  return (
    <div className="d-flex flex-row gap-2" style={{ color: '#19234F', fontSize: '16px' }}>
      <i className="bi bi-check-circle-fill" style={{ color: '#297DF2' }}></i>
      {text}
    </div>
  );
};

const PostulationItemComponent = ({
  postulation,
  index,
  responseGpt,
  psName,
  sendGTMEvent,
  setShowApplicationModal,
}: PostulationItemProps): React.ReactElement => {
  const [submitPostulationFiles, { isLoading: isLoadingFiles }] = useAddPostulationFilesMutation();
  const [psWordDocument, setPsWordDocument] = useState<File>();
  const navigate = useNavigate();

  useEffect(() => {
    if (psWordDocument) {
      handleUploadFile(psWordDocument);
    }
  }, [psWordDocument]);

  const handleUploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append('id', String(postulation.id));
    formData.append('postulation[personal_statement]', file);
    await submitPostulationFiles({ data: formData, id: postulation.id })
      .unwrap()
      .then((res) => {
        sendGTMEvent('ps_builder_add_to_application');
        navigate('/personal-statements');
        setShowApplicationModal(false);
        navigateTop();
      });
  };

  return (
    <div className={styles.postulationItem}>
      <Text>{`${index + 1}. ${postulation.program_name}`}</Text>
      {isLoadingFiles ? (
        <div className={`spinner-border ${styles.loading}`} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      ) : (
        <button
          className={`btn btn-primary nav-link ${styles.postulationButton}`}
          onClick={() =>
            generateWordDocument(responseGpt, psName, sendGTMEvent, setPsWordDocument, true)
          }
        >
          Add to application
        </button>
      )}
    </div>
  );
};

const StatementTabComponent = ({
  text,
  stepIsActive,
  completed,
  onClick,
}: TabProps): React.ReactElement => {
  return (
    <a className={`${styles.tabs} ${stepIsActive ? styles.active : ''}`} onClick={onClick}>
      {completed ? (
        <i className="bi bi-check-circle-fill" style={{ color: '#297DF2' }}>
          {' '}
        </i>
      ) : (
        <i className="bi bi-check-circle"> </i>
      )}
      {text}
    </a>
  );
};

const LabelInputComponent = ({
  keyNumber,
  setInputFunction,
  inputValue,
  placeholderText,
  setObjectFunction,
  canBeDeleted,
  deleteBulletInputOption,
  stateToChange,
  dependencyTags,
  setAvailableTags,
}: LabelInputProps): React.ReactElement => {
  const deleteInput = () => {
    deleteBulletInputOption(
      keyNumber,
      setObjectFunction,
      stateToChange,
      dependencyTags,
      setAvailableTags
    );
  };
  return (
    <div className={styles.itemLabelContainer}>
      <div className={styles.itemLabel}>{keyNumber}</div>
      <input
        className={`form-control ${styles.labelInput}`}
        type="text"
        onChange={(e) => {
          setInputFunction(keyNumber, e.target.value, setObjectFunction);
        }}
        value={inputValue}
        placeholder={placeholderText}
      />
      <button onClick={deleteInput} className={styles.deleteButton} hidden={!canBeDeleted}>
        <i className="bi bi-x" />
      </button>
    </div>
  );
};

const FillGtmEventInLocalStorage = (step: string, id: string) => {
  const personal_statement = localStorage.getItem(`personal_statement_${id}`);
  const personal_statement_json = personal_statement !== null ? JSON.parse(personal_statement) : {};
  personal_statement_json[step] = true;
  localStorage.setItem(`personal_statement_${id}`, JSON.stringify(personal_statement_json));
};

const ShouldSendGtmEvent = (step: string, id: string) => {
  const personal_statement = localStorage.getItem(`personal_statement_${id}`);
  const personal_statement_json = personal_statement !== null ? JSON.parse(personal_statement) : {};
  return personal_statement_json[step] !== true;
};

const PersonalStatementBuilder: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(({ session }: { session: SessionState }) => session);
  const navigate = useNavigate();
  const [school, setSchool] = useState<CommonHash>({ label: '', value: -1 });
  const [program, setProgram] = useState<CommonHash>({ label: '', value: -1 });
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [answers, setAnswer] = useState<CommonHash>({});
  const [generateForm, setGenerateForm] = useState<boolean>(false);
  const [schoolMotivations, setSchoolMotivations] = useState<CommonHash>({ 1: '', 2: '', 3: '' });
  const [countryMotivations, setCountryMotivations] = useState<CommonHash>({ 1: '', 2: '', 3: '' });
  const [futureSkills, setFutureSkills] = useState<CommonHash>({ 1: '', 2: '', 3: '' });
  const [personalStrengths, setPersonalStrengths] = useState<CommonHash>({ 1: '', 2: '', 3: '' });
  const [futureSkillsOptions, setFutureSkillsOptions] = useState<string[]>([]);
  const [availableFutureSkillsOptions, setAvailableFutureSkillsOptions] = useState<string[]>([]);
  const [personalStrengthsOptions, setPersonalStrengthsOptions] = useState<string[]>([]);
  const [availablePersonalStrengthsOptions, setAvailablePersonalStrengthsOptions] = useState<
    string[]
  >([]);
  const [responseGpt, setResponseGpt] = useState<string>('');
  const [psName, setPsName] = useState<string>('');
  const [availableToGenerate, setAvailableToGenerate] = useState<boolean>(false);
  const [schoolProgramStepDone, setSchoolProgramStepDone] = useState<boolean>(false);
  const [motivationsStepDone, setMotivationsStepDone] = useState<boolean>(false);
  const [futureGoalsStepDone, setFutureGoalsStepDone] = useState<boolean>(false);
  const [personalExperienceStepDone, setPersonalExperienceStepDone] = useState<boolean>(false);
  const [selfIntroductionStepDone, setSelfIntroductionStepDone] = useState<boolean>(false);
  const [progressPercentage, setProgressPercentage] = useState<number>(0);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [stepsArray, setStepsArray] = useState<boolean[]>([
    schoolProgramStepDone,
    selfIntroductionStepDone,
    motivationsStepDone,
    futureGoalsStepDone,
    personalExperienceStepDone,
  ]);
  const [firstStepWarning, setFirstStepWarning] = useState<string>('');
  const [lastSavedDate, setLastSavedDate] = useState<Date>();
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [showApplicationModal, setShowApplicationModal] = useState<boolean>(false);
  const [psDocxFile, setPsDocxFile] = useState<File>();
  const [psGeneratedLimitMessage, setPsGeneratedLimitMessage] = useState<string>('');

  const savedCallback = useRef<any>();

  const { id } = useParams();

  const [patchPersonalStatement, { data: updatedPersonalStatement, isLoading: isPatchLoading }] =
    usePatchPersonalStatementMutation();

  const [updateUser, { data: updatedUser, isLoading: isUpdateUserLoading }] =
    useUpdateUserFromPersonalStatementMutation();

  const [
    generatePersonalStatement,
    { data: generatedPersonalStatement, isLoading: isGenerateLoading },
  ] = useGeneratePersonalStatementMutation();

  const { data: personalStatement, isLoading } = useGetPersonalStatementQuery(parseInt(id || ''));

  const {
    data: schools,
    isLoading: schoolsLoading,
    isFetching: schoolsFetching,
  } = useGetSchoolsPersonalStatementQuery();
  const { data: questions } = useGetPersonalStatementQuestionsQuery({});

  const [
    getPrograms,
    { data: programs, isLoading: programsLoading, isFetching: programsFetching },
  ] = useLazyGetSchoolProgramsPersonalStatementQuery();
  const [
    getPostulations,
    { data: postulations, isLoading: postulationsLoading, isFetching: postulationsFetching },
  ] = useLazyGetPostulationsListQuery();

  const [uploadPersonalStatementFileMail, { isLoading: isLoadingFiles }] =
    useSendPersonalStatementMailFileMutation();

  const arrayOfUnpermittedKeys = [
    'have_working_experience',
    'gpt_response',
    'user',
    'updated_at',
    'formatted_name',
    'status',
  ];

  const tooltip = (
    <Tooltip id="tooltip">
      You must complete all the steps to generate the personal statement
    </Tooltip>
  );

  const handleShowApplicationModal = () => {
    setShowApplicationModal(true);
    getPostulations();
  };

  const generateHash = (hash: CommonHash, setField: any) => {
    let newHash: CommonHash = {};
    if ('id' in hash) {
      newHash = {
        content: hash.content,
        id: hash.id,
        personal_statement_question_id: hash.question_key,
      };
    } else {
      newHash = { content: { 1: '', 2: '', 3: '' } };
    }
    setField(newHash);
  };
  const activeStep = (step: number) => {
    return currentStep == step;
  };

  const onPrevStep = () => {
    if (currentStep) {
      setCurrentStep(currentStep - 1);
      navigateTop();
    } else {
      navigate(-1);
    }
  };

  const onNextStep = () => {
    if (currentStep != 5) {
      if (stepsArray[currentStep] && ShouldSendGtmEvent(currentStep.toString(), id || '')) {
        FillGtmEventInLocalStorage(currentStep.toString(), id || '');
        sendStepCompletedGTMEvent(currentStep);
      }
      if (activeStep(0) && !schoolProgramStepDone) {
        setFirstStepWarning('You must complete the first step');
      } else {
        handleSavePersonalStatement();
        setCurrentStep(currentStep + 1);
        setFirstStepWarning('');
      }
      navigateTop();
    } else {
      setGenerateForm(true);
    }
  };

  const handleChangeStep = (stepNumber: number) => {
    if (stepsArray[currentStep] && ShouldSendGtmEvent(currentStep.toString(), id || '')) {
      FillGtmEventInLocalStorage(currentStep.toString(), id || '');
      sendStepCompletedGTMEvent(currentStep);
    }
    if (activeStep(0) && !schoolProgramStepDone) {
      setFirstStepWarning('You must complete the first step');
    } else {
      handleSavePersonalStatement();
      setCurrentStep(stepNumber);
      setFirstStepWarning('');
    }
    navigateTop();
  };

  const buildEntity = (
    key: string,
    answersHash: CommonHash,
    setEntity: React.Dispatch<React.SetStateAction<any>>
  ) => {
    if (key === 'school_name' && answersHash[key]) {
      const entityFound = schools?.data.find((c) => c.name === answersHash[key]);
      setEntity({
        label: answersHash[key],
        value: entityFound ? entityFound.id : 0,
      });
    } else if (key === 'program_name') {
      const entityFound = programs?.data.find((c) => c.name === answersHash[key]);
      setEntity({
        label: answersHash[key],
        value: entityFound ? entityFound.id : 0,
      });
    }
  };

  const buildAnswersHash = (personalStatement: CommonHash) => {
    const answersHash: CommonHash = {};
    Object.keys(personalStatement).forEach(async (key) => {
      if (key === 'personal_statement_answers') {
        personalStatement[key] &&
          personalStatement[key].forEach((answer: any) => {
            answersHash[answer.question_key] = { content: answer.content, id: answer.id } || {};
          });
      } else if (key !== 'id' && key !== 'name' && key !== 'bookmarked') {
        answersHash[key] = personalStatement[key] || '';
      }
    });
    setAnswer((a: any) => ({ ...a, ...answersHash }));
  };

  const setInputOption = (
    key: string,
    value: any,
    setStateFunction: (value: React.SetStateAction<CommonHash>) => void
  ) => {
    setStateFunction((a: any) => ({
      ...a,
      content: {
        ...a.content,
        [key]: value,
      },
    }));
  };

  const deleteBulletInputOption = (
    key: string,
    setStateFunction: (value: React.SetStateAction<CommonHash>) => void,
    stateToChange: CommonHash,
    dependencyTags?: string[],
    setAvailableTags?: (value: React.SetStateAction<string[]>) => void
  ) => {
    const newContent = { ...stateToChange.content };
    if (dependencyTags && setAvailableTags) {
      if (dependencyTags.includes(newContent[key])) {
        setAvailableTags((tags) => [...tags, newContent[key]]);
      }
    }
    delete newContent[key];
    const newContentKeys = Object.keys(newContent);
    const newContentValues = Object.values(newContent);
    const newContentHash: CommonHash = {};
    newContentKeys.forEach((key, index) => {
      newContentHash[index + 1] = newContentValues[index];
    });
    setStateFunction((a: any) => ({
      ...a,
      content: newContentHash,
    }));
  };

  const handleAddBulletOption = (
    option: string,
    setStateOptionsFunction: (value: React.SetStateAction<string[]>) => void,
    setStateFunction: (value: React.SetStateAction<CommonHash>) => void,
    stateToChange: CommonHash
  ) => {
    const indexEmpty = Object.keys(stateToChange['content']).find(
      (x) => stateToChange['content'][x] === ''
    );
    setStateOptionsFunction((options) =>
      options.filter((answerOption: string) => answerOption !== option)
    );
    if (indexEmpty) {
      setInputOption(indexEmpty, option, setStateFunction);
    } else {
      setInputOption(
        (Object.keys(stateToChange['content']).length + 1).toString(),
        option,
        setStateFunction
      );
    }
  };

  const getProgressMessage = (percentage: number) => {
    if (percentage === 0) {
      return "Let's get started! Just a few details to go!";
    } else if (percentage === 25) {
      return "Making progress! You're almost halfway there";
    } else if (percentage === 50) {
      return "You're doing great! 50% complete!";
    } else if (percentage === 75) {
      return 'So close! Just a few more pieces to complete!';
    } else {
      return 'Almost there! Just a final push to go!';
    }
  };

  const sendGTMEvent = (eventName: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: eventName,
      },
    });
  };

  const handleSaveForm = () => {
    if (!schoolProgramStepDone) {
      setFirstStepWarning('You must complete the first step');
    } else {
      handleSavePersonalStatement();
      navigate('/personal-statements');
      sendGTMEvent('ps_builder_save_and_leave');
    }
  };

  const handleSavePersonalStatement = () => {
    if (!schoolProgramStepDone) {
      setFirstStepWarning('You must complete the first step');
    } else {
      if (currentStep < 6) {
        const values: any = { personal_statement: { personal_statement_answers_attributes: [] } };
        const user_values: any = { user: { ...answers.user } };
        Object.keys(answers).forEach((key) => {
          if (key === 'program_name' || key === 'school_name') {
            values.personal_statement = { ...values.personal_statement, [key]: answers[key] };
          } else if (key === 'status' && answers[key] === 'generated' && !availableToGenerate) {
            values.personal_statement = { ...values.personal_statement, [key]: 'pending' };
          } else if (!arrayOfUnpermittedKeys.includes(key) && 'content' in answers[key]) {
            values.personal_statement.personal_statement_answers_attributes.push({
              ...answers[key],
              personal_statement_question_id: key,
            });
          }
        });
        patchPersonalStatement({ data: values, id: parseInt(id || '') }).then((response: any) => {
          setLastSavedDate(new Date());
        });
        updateUser({ data: user_values }).then((response: any) => {
          console.log(response);
        });
      }
    }
  };

  useEffect(() => {
    if (psDocxFile) {
      handleUploadPersonalStatement(psDocxFile);
    }
  }, [psDocxFile]);

  useEffect(() => {
    if (responseGpt && psName) {
      generateWordDocument(responseGpt, psName, sendGTMEvent, setPsDocxFile, false);
    }
  }, [responseGpt, psName]);

  const handleUploadPersonalStatement = async (file: File) => {
    if (personalStatement?.data.id) {
      const formData = new FormData();
      // formData.append('id', String(personalStatement?.data.id));
      formData.append('personal_statement[docx_file]', file);
      await uploadPersonalStatementFileMail({ data: formData, id: personalStatement.data.id })
        .unwrap()
        .then((res) => {
          console.log(res);
        });
    }
  };

  useEffect(() => {
    savedCallback.current = handleSavePersonalStatement;
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      savedCallback.current();
    }, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const sendStepCompletedGTMEvent = (stepNumber: number) => {
    if (stepNumber === 0) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'ga_event',
          eventName: 'ps_builder_step_completed_school_program',
        },
      });
    } else if (stepNumber === 1) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'ga_event',
          eventName: 'ps_builder_step_completed_self_introduction',
        },
      });
    } else if (stepNumber === 2) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'ga_event',
          eventName: 'ps_builder_step_completed_motivation',
        },
      });
    } else if (stepNumber === 3) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'ga_event',
          eventName: 'ps_builder_step_completed_personal_experience',
        },
      });
    }
  };

  const textExists = (text: string) => {
    return !!(text && text.length > 0);
  };

  const schoolMotivationsQuestion = useMemo(
    () =>
      questions?.data.find(
        (question: PersonalStatementQuestion) => question.key === 'school_motivations'
      ),
    [questions]
  );

  const countryMotivationsQuestion = useMemo(
    () =>
      questions?.data.find(
        (question: PersonalStatementQuestion) => question.key === 'country_motivations'
      ),
    [questions]
  );

  const futureSkillsQuestion = useMemo(
    () =>
      questions?.data.find(
        (question: PersonalStatementQuestion) => question.key === 'future_skills'
      ),
    [questions]
  );

  const skillsDevelopmentQuestion = useMemo(
    () =>
      questions?.data.find(
        (question: PersonalStatementQuestion) => question.key === 'skills_development_method'
      ),
    [questions]
  );

  const careerAspirationsQuestion = useMemo(
    () =>
      questions?.data.find(
        (question: PersonalStatementQuestion) => question.key === 'career_aspirations'
      ),
    [questions]
  );

  const personalStrengthsQuestion = useMemo(
    () =>
      questions?.data.find(
        (question: PersonalStatementQuestion) => question.key === 'personal_strengths'
      ),
    [questions]
  );

  const showFutureSkillsOptions = useMemo(
    () =>
      !!(
        futureSkillsQuestion?.answer_options &&
        futureSkillsQuestion?.answer_options.length &&
        futureSkillsQuestion?.answer_options.length > 0
      ),
    [questions]
  );

  const showPersonalStrengthsOptions = useMemo(
    () =>
      !!(
        personalStrengthsQuestion?.answer_options &&
        personalStrengthsQuestion?.answer_options.length &&
        personalStrengthsQuestion?.answer_options.length > 0
      ),
    [questions]
  );

  const personalExperienceQuestions = useMemo(
    () =>
      questions?.data.filter(
        (question) =>
          question.section === 'personal_experience' && question.key !== 'personal_strengths'
      ),
    [questions]
  );

  const optionalQuestion = useMemo(
    () => questions?.data.filter((question) => question.section === 'optional_question'),
    [questions]
  );
  useEffect(() => {
    if (user) {
      const hash = { user: userAnswers(user, answers) };
      setAnswer((a: any) => ({ ...a, ...hash }));
    }
  }, [user]);

  useEffect(() => {
    if (updatedUser && user) {
      dispatch(setUser({ ...updatedUser.data, accessToken: user?.accessToken }));
      navigateTop();
    }
  }, [updatedUser]);

  useEffect(() => {
    if (generateForm) {
      sendGTMEvent('ps_builder_statement_generating');
      const values: any = { personal_statement: { personal_statement_answers_attributes: [] } };
      const user_values: any = { user: { ...answers.user } };
      Object.keys(answers).forEach((key) => {
        if (key === 'program_name' || key === 'school_name') {
          values.personal_statement = { ...values.personal_statement, [key]: answers[key] };
        } else if (!arrayOfUnpermittedKeys.includes(key) && 'content' in answers[key]) {
          values.personal_statement.personal_statement_answers_attributes.push({
            ...answers[key],
            personal_statement_question_id: key,
          });
        }
      });
      generatePersonalStatement({ data: values, id: parseInt(id || '') }).then((response: any) => {
        if (response['error'] && response['error'].status === 412) {
          setPsGeneratedLimitMessage(
            'You must wait a few minutes to generate a new Personal Statement'
          );
        } else {
          setCurrentStep(6);
          setResponseGpt(response.data.data.gpt_response);
          setPsName(response.data.data.formatted_name);
          setPsGeneratedLimitMessage('');
          if (textExists(answers['gpt_response'])) {
            sendGTMEvent('ps_builder_statement_regenerated');
          } else {
            sendGTMEvent('ps_builder_statement_generated');
          }
        }
        // implement here navigate
      });
      updateUser({ data: user_values }).then((response: any) => {
        console.log(response);
      });
    }
  }, [generateForm]);

  useEffect(() => {
    if (personalStatement) {
      generateHash(
        personalStatement?.data.personal_statement_answers?.find(
          (answer: any) => answer.question_key === 'school_motivations'
        ) || {},
        setSchoolMotivations
      );
      generateHash(
        personalStatement?.data.personal_statement_answers?.find(
          (answer: any) => answer.question_key === 'country_motivations'
        ) || {},
        setCountryMotivations
      );
      generateHash(
        personalStatement?.data.personal_statement_answers?.find(
          (answer: any) => answer.question_key === 'future_skills'
        ) || {},
        setFutureSkills
      );
      generateHash(
        personalStatement?.data.personal_statement_answers?.find(
          (answer: any) => answer.question_key === 'personal_strengths'
        ) || {},
        setPersonalStrengths
      );
    }
  }, [personalStatement]);

  useEffect(() => {
    if (personalStatement) {
      buildAnswersHash(personalStatement.data);
    }
  }, [personalStatement]);

  useEffect(() => {
    if (
      schools?.data &&
      personalStatement?.data['school_name'] &&
      personalStatement?.data['school_name'].length > 0
    ) {
      buildEntity('school_name', personalStatement?.data, setSchool);
    }
  }, [schools, personalStatement]);

  useEffect(() => {
    if (
      programs?.data &&
      personalStatement?.data['program_name'] &&
      personalStatement?.data['program_name'].length > 0
    ) {
      // caso en que viene cargado el personal statement
      if (school.label !== personalStatement?.data['school_name']) {
        // cambio la school de la que venia
        setProgram({ label: '', value: -1 });
        setAnswer((a: any) => ({ ...a, program_name: '' }));
      } else {
        buildEntity('program_name', personalStatement?.data, setProgram);
        setAnswer((a: any) => ({ ...a, program_name: personalStatement?.data['program_name'] }));
      }
    } else if (
      !programs &&
      personalStatement?.data['program_name'] &&
      personalStatement?.data['program_name'].length > 0
    ) {
      //caso que viene con personal statement y es other en school
      buildEntity('program_name', personalStatement?.data, setProgram);
    }
  }, [programs, personalStatement]);

  useEffect(() => {
    if (school.value > 0) {
      getPrograms(school.value);
    }
    if (school.value === 0 && school.label !== answers['school_name']) {
      setProgram({ label: '', value: 0 });
      setAnswer((a: any) => ({ ...a, program_name: '' }));
    }
  }, [school]);

  useEffect(() => {
    if (futureSkillsQuestion) {
      setFutureSkillsOptions(futureSkillsQuestion.answer_options);
      if (futureSkills.content) {
        setAvailableFutureSkillsOptions(
          futureSkillsQuestion.answer_options.filter(
            (option: string) => !Object.values(futureSkills.content).includes(option)
          )
        );
      } else {
        setAvailableFutureSkillsOptions(futureSkillsQuestion.answer_options);
      }
    }
  }, [futureSkillsQuestion]);

  useEffect(() => {
    if (personalStrengthsQuestion) {
      setPersonalStrengthsOptions(personalStrengthsQuestion.answer_options);
      if (personalStrengths.content) {
        setAvailablePersonalStrengthsOptions(
          personalStrengthsQuestion.answer_options.filter(
            (option: string) => !Object.values(personalStrengths.content).includes(option)
          )
        );
      } else setAvailablePersonalStrengthsOptions(personalStrengthsQuestion.answer_options);
    }
  }, [personalStrengthsQuestion]);

  useEffect(() => {
    setAnswer((a: any) => ({ ...a, school_motivations: schoolMotivations }));
  }, [schoolMotivations]);
  useEffect(() => {
    setAnswer((a: any) => ({ ...a, country_motivations: countryMotivations }));
  }, [countryMotivations]);

  useEffect(() => {
    setAnswer((a: any) => ({ ...a, future_skills: futureSkills }));
  }, [futureSkills]);

  useEffect(() => {
    setAnswer((a: any) => ({ ...a, personal_strengths: personalStrengths }));
  }, [personalStrengths]);

  useEffect(() => {
    setSchoolProgramStepDone(
      textExists(answers['school_name']) && textExists(answers['program_name'])
    );
    if (schoolMotivations?.content) {
      setMotivationsStepDone(
        Object.values(schoolMotivations.content).filter((a: any) => a.length > 0).length > 2 &&
          Object.values(countryMotivations.content).filter((a: any) => a.length > 0).length > 2
      );
    }
    if (futureSkills?.content) {
      setFutureGoalsStepDone(
        Object.values(futureSkills.content).filter((a: any) => a.length > 0).length > 2 &&
          textExists(answers['skills_development_method']['content']['text']) &&
          textExists(answers['career_aspirations']['content']['text'])
      );
    }

    if (personalStrengths?.content) {
      setPersonalExperienceStepDone(
        Object.values(personalStrengths.content).filter((a: any) => a.length > 0).length > 2 &&
          textExists(answers['strengths_demonstration']['content']['text']) &&
          textExists(answers['overcame_challenge']['content']['text']) &&
          textExists(answers['living_studying_abroad']['content']['text'])
      );
    }
    if (answers['user'] !== undefined) {
      setSelfIntroductionStepDone(
        textExists(answers['user']['name']) &&
          answers['user']['academic_history'] !== undefined &&
          answers['user']['academic_history'].length > 0 &&
          answers['user']['academic_history'].some(
            (history: CommonHash) =>
              textExists(history['school_name']) &&
              textExists(history['degree_type']) &&
              textExists(history['subject_type'])
          ) &&
          ((answers['user']['work_history'] !== undefined &&
            answers['user']['work_history'].length > 0 &&
            answers['user']['work_history'].some(
              (history: CommonHash) =>
                textExists(history['job_title']) &&
                textExists(history['organization_name']) &&
                textExists(history['working_area']) &&
                textExists(history['working_location'])
            )) ||
            answers['user']['have_working_experience'] === false)
      );
    }
  }, [answers]);

  useEffect(() => {
    setAvailableToGenerate(
      schoolProgramStepDone &&
        motivationsStepDone &&
        futureGoalsStepDone &&
        personalExperienceStepDone &&
        selfIntroductionStepDone
    );
    setProgressPercentage(
      ([
        schoolProgramStepDone,
        motivationsStepDone,
        futureGoalsStepDone,
        personalExperienceStepDone,
        selfIntroductionStepDone,
      ].filter(Boolean).length /
        5) *
        100
    );
    setStepsArray([
      schoolProgramStepDone,
      selfIntroductionStepDone,
      motivationsStepDone,
      futureGoalsStepDone,
      personalExperienceStepDone,
    ]);
  }, [
    schoolProgramStepDone,
    selfIntroductionStepDone,
    motivationsStepDone,
    futureGoalsStepDone,
    personalExperienceStepDone,
  ]);

  useEffect(() => {
    setProgressMessage(getProgressMessage(progressPercentage));
  }, [progressPercentage]);

  // useEffect(() => {
  //   for (let i = 0; i < stepsArray.length; i++) {
  //     if (stepsArray[i]) {
  //       FillGtmEventInLocalStorage(i.toString(), id || '');
  //     }
  //   }
  // }, [stepsArray]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className={styles.statementBuilderContainer}>
      {isGenerateLoading ? (
        <PersonalStatementLoader />
      ) : currentStep === 6 ? (
        <div className={styles.personalStatementGenerated}>
          <Text size={30} weight={700} inlineStyle={{ color: '#193676', textAlign: 'center' }}>
            Voila! We successfully generated personal statement for you!
          </Text>
          <div className={styles.checklistContainer}>
            <RobotShadowIcon />
            <div className={styles.checklistContent}>
              <Text size={20} weight={600} inlineStyle={{ color: '#193676' }}>
                Here is a checklist for a brilliant personal statement
              </Text>
              <ChecklistItemComponent
                text={'Personalized and relevant content tailored to your university and program'}
              />
              <ChecklistItemComponent
                text={'Proper structure that reflects a well-defined outline'}
              />
              <ChecklistItemComponent text={'Free of grammatical and spelling errors'} />
            </div>
          </div>
          <Modal
            show={showDownloadModal}
            onHide={() => {
              setShowDownloadModal(false);
            }}
          >
            <Modal.Body>Download as</Modal.Body>
            <Modal.Footer className={styles.modalDownloadFooter}>
              <Button
                variant="primary"
                onClick={() => generatePdf(responseGpt, psName, sendGTMEvent, setShowDownloadModal)}
              >
                PDF
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  generateWordDocument(responseGpt, psName, sendGTMEvent, undefined, true);
                  setShowDownloadModal(false);
                }}
              >
                Word Document
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  navigator.clipboard.writeText(responseGpt);
                  sendGTMEvent('ps_builder_statement_exported_copy');
                  setShowDownloadModal(false);
                }}
              >
                Copy Text
              </Button>
            </Modal.Footer>
          </Modal>
          <Modal
            show={showApplicationModal}
            onHide={() => {
              setShowApplicationModal(false);
            }}
            // dialogClassName="modal-80w"
            size="lg"
          >
            <Modal.Body>Add to application</Modal.Body>
            <Modal.Footer className={styles.modalFooterApplications}>
              {postulations?.data.length > 0 &&
                postulations?.data.map((postulation: Postulation, index: number) => (
                  <PostulationItemComponent
                    postulation={postulation}
                    index={index}
                    key={postulation.id}
                    responseGpt={responseGpt}
                    psName={psName}
                    sendGTMEvent={sendGTMEvent}
                    setShowApplicationModal={setShowApplicationModal}
                  />
                ))}
            </Modal.Footer>
          </Modal>
          <div className={styles.textGeneratedContainer}>
            <Text size={14} weight={400} inlineStyle={{ color: '#000' }}>
              <ReactMarkdown>{responseGpt}</ReactMarkdown>
            </Text>
          </div>
          <div className={styles.generatedButtons}>
            <button
              className={'btn btn-primary nav-link'}
              onClick={() => setShowDownloadModal(true)}
            >
              Download
            </button>
            <button
              className={`btn btn-primary nav-link ${styles.postulationButton}`}
              onClick={handleShowApplicationModal}
            >
              Add to my application
            </button>
          </div>
          <hr className={styles.horizontalLine} />
          <div className="d-flex flex-column align-items-center w-100 gap-5">
            <Text size={20} weight={600} inlineStyle={{ color: '#19234F' }}>
              Need more guidance on improving your personal statement?
            </Text>
            <div className={styles.expertBanner}>
              <div className={styles.expertInfo}>
                <div className={styles.contactInfo}>
                  <div className={styles.expertPhoto}>
                    <i className="bi bi-person-circle"></i>
                  </div>
                  <div className="d-flex flex-column justify-content-center">
                    <Text size={16} weight={600}>
                      Patricia Miraflores
                    </Text>
                    <Text size={12} weight={400} color="secondary">
                      patricia@unifyapply.com
                    </Text>
                  </div>
                </div>
                <button
                  className={`btn btn-primary nav-link ${styles.expertButton}`}
                  onClick={() => {
                    window.open('/experts/request-meeting');
                    sendGTMEvent('ps_builder_get_expert_help');
                  }}
                >
                  Get expert writing help
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={`col-8 ${styles.statementBuilderContent}`}>
            <div className={styles.progressBanner}>
              <StepsRobot />
              <div className={styles.bannerContent}>
                <Text size={16} weight={400} inlineStyle={{ color: '#193676' }}>
                  {progressMessage}
                </Text>
                <div className={styles.barContainer}>
                  <div className={styles.statementProgressBar}>
                    <div
                      className={styles.barProgress}
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                  <Text size={16} weight={600} inlineStyle={{ color: '#297DF2' }}>
                    {`${progressPercentage} %`}
                  </Text>
                </div>
              </div>
            </div>
            {firstStepWarning && (
              <div className={styles.warningLabel} style={{ marginTop: '10px' }}>
                {firstStepWarning}
              </div>
            )}
            <div className={styles.questionsContainer}>
              {activeStep(0) && (
                <UniversityProgramStep
                  setInputOption={setInputOption}
                  setAnswers={setAnswer}
                  school={school}
                  schoolsLoading={schoolsLoading}
                  schoolsFetching={schoolsFetching}
                  schools={schools}
                  program={program}
                  programsLoading={programsLoading}
                  programsFetching={programsFetching}
                  programs={programs}
                  setSchool={setSchool}
                  setProgram={setProgram}
                />
              )}
              {activeStep(1) && <SelfIntroduction answers={answers} setAnswer={setAnswer} />}
              {activeStep(2) && (
                <MotivationsStep
                  setInputOption={setInputOption}
                  schoolMotivationsQuestion={schoolMotivationsQuestion}
                  schoolMotivations={schoolMotivations}
                  setSchoolMotivations={setSchoolMotivations}
                  countryMotivationsQuestion={countryMotivationsQuestion}
                  countryMotivations={countryMotivations}
                  setCountryMotivations={setCountryMotivations}
                  deleteBulletInputOption={deleteBulletInputOption}
                />
              )}
              {activeStep(3) && (
                <FutureSkillsStep
                  setInputOption={setInputOption}
                  futureSkills={futureSkills}
                  futureSkillsQuestion={futureSkillsQuestion}
                  setFutureSkills={setFutureSkills}
                  setFutureSkillsOptions={setAvailableFutureSkillsOptions}
                  showFutureSkillsOptions={showFutureSkillsOptions}
                  futureSkillsOptions={availableFutureSkillsOptions}
                  handleAddBulletOption={handleAddBulletOption}
                  skillsDevelopmentQuestion={skillsDevelopmentQuestion}
                  careerAspirationsQuestion={careerAspirationsQuestion}
                  answers={answers}
                  setAnswer={setAnswer}
                  deleteBulletInputOption={deleteBulletInputOption}
                  dependencyTags={futureSkillsOptions}
                  setAvailableTags={setAvailableFutureSkillsOptions}
                />
              )}
              {activeStep(4) && (
                <PersonalExperience
                  questions={personalExperienceQuestions || []}
                  answers={answers}
                  setAnswer={setAnswer}
                  personalStrengthsQuestion={personalStrengthsQuestion}
                  personalStrengths={personalStrengths}
                  setPersonalStrengths={setPersonalStrengths}
                  setPersonalStrengthsOptions={setAvailablePersonalStrengthsOptions}
                  setInputOption={setInputOption}
                  showPersonalStrengthsOptions={showPersonalStrengthsOptions}
                  personalStrengthsOptions={availablePersonalStrengthsOptions}
                  handleAddBulletOption={handleAddBulletOption}
                  deleteBulletInputOption={deleteBulletInputOption}
                  dependencyTags={personalStrengthsOptions}
                  setAvailableTags={setAvailablePersonalStrengthsOptions}
                />
              )}
              {activeStep(5) && (
                <OptionalQuestion
                  questions={optionalQuestion || []}
                  answers={answers}
                  setAnswer={setAnswer}
                />
              )}
              {activeStep(6) && responseGpt && (
                <div className={styles.gptResponse}>
                  <Text size={16} weight={400} inlineStyle={{ color: '#193676' }}>
                    {responseGpt}
                  </Text>
                </div>
              )}
            </div>
            <div className={styles.statementButtons}>
              <button
                className={`btn btn-primary nav-link ${styles.prevButton}`}
                onClick={() => onPrevStep()}
              >
                Previous
              </button>
              <button
                className={`btn btn-primary nav-link ${styles.nextButton}`}
                onClick={() => onNextStep()}
              >
                {currentStep == 5 ? 'Generate' : 'Next'}
              </button>
            </div>
          </div>
          <div className={`col-4 ${styles.sideColumn}`}>
            <div className={styles.statementTabs}>
              <StatementTabComponent
                text={'University & Program'}
                stepIsActive={activeStep(0)}
                completed={schoolProgramStepDone}
                onClick={() => handleChangeStep(0)}
              />
              <StatementTabComponent
                text={'Self introduction'}
                stepIsActive={activeStep(1)}
                completed={selfIntroductionStepDone}
                onClick={() => handleChangeStep(1)}
              />
              <StatementTabComponent
                text={'Motivation'}
                stepIsActive={activeStep(2)}
                completed={motivationsStepDone}
                onClick={() => handleChangeStep(2)}
              />
              <StatementTabComponent
                text={'Future goals'}
                stepIsActive={activeStep(3)}
                completed={futureGoalsStepDone}
                onClick={() => handleChangeStep(3)}
              />
              <StatementTabComponent
                text={'Personal experience'}
                stepIsActive={activeStep(4)}
                completed={personalExperienceStepDone}
                onClick={() => handleChangeStep(4)}
              />
              {lastSavedDate && (
                <div className={styles.lastSavedDate}>
                  {`Last saved at: ${lastSavedDate.toLocaleString('en-GB')}`}
                </div>
              )}
              {availableToGenerate ? (
                <>
                  <button
                    className={`btn btn-primary nav-link ${styles.generateButton}`}
                    onClick={() => setGenerateForm(true)}
                  >
                    Generate
                  </button>
                  {psGeneratedLimitMessage && (
                    <div className={styles.warningLabel}>{psGeneratedLimitMessage}</div>
                  )}
                </>
              ) : (
                <OverlayTrigger placement="left" overlay={tooltip}>
                  <button className={`btn btn-primary nav-link ${styles.disabledGenerateButton}`}>
                    Generate
                  </button>
                </OverlayTrigger>
              )}
              <button
                className={`btn btn-primary nav-link ${styles.saveButton}`}
                onClick={() => handleSaveForm()}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export { PersonalStatementBuilder, LabelInputComponent, PostulationItemComponent };
